/* eslint-disable no-restricted-imports */
import React, {useEffect, useRef, useState} from 'react'
import styles from './UIGameEnd.module.scss'
import {UIGameEndProvider, UIGameEndProviderProps, useUIGameEndContext} from '../../contexts/UIGameEndContext'
import {UIGameEndHeader} from './UIGameEndHeader'
import {UIGameEndContent} from './UIGameEndContent'
import {UIGameEndFooter} from './UIGameEndFooter'
import {UIGameEndRankedScreen} from './UIGameEndRankedScreen'
import Confetti from 'react-confetti'
import {Sound} from '../../utils/sound'
import {ClientUserState} from '../../../../../code/client/shared/scripts/ClientUserState'
import {ViewPopupEndgameSignup} from '../../../../../code/client/shared/scripts/ui_popup/ViewPopupEndgameSignup'
import {StorageWrapper} from '../../utils/storage'
import {useGameStore} from '../../store/GameStoreContext'
import {useShareGameResults} from '../../store/selectors/useShareGameResults'
import {useIsReplay} from '../../store/selectors/useIsReplay'
import {useUIGlobalContext} from '@colonist/ui'

type GlobalGameEndProviderProps = Omit<UIGameEndProviderProps, 'children'>
let globalGameEndProviderProps: undefined | ((gameEndProviderProps: GlobalGameEndProviderProps) => void)
export function setGlobalGameEndProviderProps(gameEndProviderProps: GlobalGameEndProviderProps) {
    if(globalGameEndProviderProps) globalGameEndProviderProps(gameEndProviderProps)
}

let globalGameEndNumberOfPlayersJoinedRematch: undefined | ((numberOfPlayersJoinedRematch: number) => void)
export function setGlobalGameEndNumberOfPlayersJoinedRematch(numberOfPlayersJoinedRematch: number) {
    if(globalGameEndNumberOfPlayersJoinedRematch) globalGameEndNumberOfPlayersJoinedRematch(numberOfPlayersJoinedRematch)
}

export const UIGameEnd = () => {
    const [gameEndProviderProps, setGameEndProviderProps] = useState<GlobalGameEndProviderProps | null>(null)
    const [numberOfPlayersJoinedRematch, setNumberOfPlayersJoinedRematch] = useState(0)
    const isVisible = useGameStore((state) => state.gameEnd.isVisible)
    const hasShownAnimationsRef = useRef(false)

    globalGameEndProviderProps = setGameEndProviderProps
    globalGameEndNumberOfPlayersJoinedRematch = setNumberOfPlayersJoinedRematch

    if(!gameEndProviderProps) return null
    if(!isVisible) return null

    const isAnimationsDisabled = hasShownAnimationsRef.current
    if(isVisible) hasShownAnimationsRef.current = true

    return <UIGameEndProvider {...gameEndProviderProps} numberOfPlayersJoinedRematch={numberOfPlayersJoinedRematch} >
        <UIGameEndComponent defaultDisableAnimation={isAnimationsDisabled}/>
    </UIGameEndProvider>
}

export interface UIGameEndComponentProps {
    defaultDisableAnimation: boolean
}

export function UIGameEndComponent({
    defaultDisableAnimation,
}: UIGameEndComponentProps) {
    const isReplay = useIsReplay()
    const {isUserOnDiscord, isUserOnMobileAppOrWeb} = useUIGlobalContext()
    const {isPlayerHasRankedState, isMyColorWinner} = useUIGameEndContext()
    const shareGameResults = useShareGameResults()
    const [showRankedScreen, setShowRankedScreen] = useState(isPlayerHasRankedState && !isReplay)
    const [runConfetti, setRunConfetti] = useState(false)
    const safeAreaTop = StorageWrapper.getStatusBarHeight()

    const disableAnimation = defaultDisableAnimation || isReplay || !isMyColorWinner

    const playVictorySound = () => {
        // Original issue: https://github.com/colonistio/katan/pull/9949
        // TODO Eventually replace the victory sound with the end game music and play for all players
        // Sound.playSound(Sound.SoundType.MusicEgoSum)

        if(!isMyColorWinner) return
        if(isReplay) return

        Sound.playSound(Sound.SoundType.Victory)
    }

    const showSignupPopup = () => {
        if(isUserOnDiscord) return
        if(isUserOnMobileAppOrWeb) return
        if(ClientUserState.userState?.isLoggedIn) return

        const gameCountMilestones = [5, 10, 25]
        const postMilestonesInterval = 50
        const completedGameCount = (ClientUserState.userState?.totalCompletedGameCount ?? 0) + 1
        const isHardCodedGameCount = gameCountMilestones.includes(completedGameCount)
        const passedTheHardcodedGameCounts = completedGameCount > Math.max(...gameCountMilestones)
        const hitMilestoneInterval = completedGameCount % postMilestonesInterval == 0

        const shouldShowSignupPopup = isHardCodedGameCount || (passedTheHardcodedGameCounts && hitMilestoneInterval)
        if(!shouldShowSignupPopup) return

        const popup = new ViewPopupEndgameSignup(completedGameCount)
        popup.show()
    }

    useEffect(() => {
        if(!disableAnimation) {
            setRunConfetti(true)
            playVictorySound()
        }
        showSignupPopup()
    }, [])

    return (
        <div className={styles.container} style={{paddingTop: Math.max(safeAreaTop ?? 0, 24)}}>
            {isMyColorWinner && !isReplay && runConfetti &&
                <Confetti
                    className={styles.confetti}
                    initialVelocityY={-10}
                    numberOfPieces={175}
                    recycle={false}/>
            }
            <UIGameEndHeader
                disableAnimation={disableAnimation}
                onClickShareGameResults={shareGameResults}
            />
            {showRankedScreen ? <UIGameEndRankedScreen
                disableAnimation={disableAnimation}
                onContinue={() => {
                    setShowRankedScreen(false)
                }}/> : <>
                <UIGameEndContent disableAnimation={disableAnimation}/>
                <UIGameEndFooter disableAnimation={disableAnimation}/>
            </>}
        </div>
    )
}
